// 🤖 COMPONENTE ABA ANÁLISE IA
// Sistema: HEPATO TX RONDÔNIA
// Arquivo: src/modules/ia-analise/components/AbaAnaliseIA.tsx
// VERSÃO 2.0 - Com histórico de análises e achados de imagem

import React, { useState, useEffect } from "react";
import {
  separarPartes,
  TabelaMarkdown,
} from "../../../shared/components/TextoAnaliseMarkdown";
import { minimizarTextoMECR } from "../services/minimizarExemploMECR";
import { CATEGORIAS_ERRO_CALIBRACAO } from "../constants/categoriasErro";
import {
  Bot,
  Clock,
  X,
  BarChart2,
  Camera,
  AlertTriangle,
  Lightbulb,
  Stethoscope,
  FlaskConical,
  ClipboardList,
  Target,
  Hospital,
  Bean,
  ScrollText,
  MessageCircle,
  Sparkles,
  Droplet,
  RefreshCw,
  ThumbsUp,
  ThumbsDown,
  MinusCircle,
  Send,
  Scale,
} from "lucide-react";
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  doc,
  updateDoc,
  Timestamp,
} from "firebase/firestore";
import { db } from "../../../lib/firebase";
import { Paciente } from "../../../types";
import { useAuth } from "../../auth/context/AuthContext";
import { ehMedico, podeEscreverClinico } from "../../../shared/utils/permissoesCategoria";
import { useIAAnaliseContext } from "../context/IAAnaliseContext";
import { PainelProveniencia } from "./PainelProveniencia";
import { PainelDelta } from "./PainelDelta";
import { theme } from "../../../shared/styles/theme";
import { AnaliseIA } from "../types";
import { ChatDiscussaoCaso } from "./ChatDiscussaoCaso";
import { LaudoEspecial } from "./LaudoEspecial";
import SelosStatus from "./SelosStatus";
// B351 (AI_153) — política do few-shot por modalidade (fonte única + testes).
import { modalidadeUsaFewShotMECR } from "../services/politicaFewShotMECR";

interface AbaAnaliseIAProps {
  paciente: Paciente;
}

export function AbaAnaliseIA({ paciente }: AbaAnaliseIAProps) {
  const {
    loading,
    erro,
    progresso,
    rascunho,
    analiseAtual,
    gerarAnalise,
    verificarCache,
    buscarUltimaAnalise,
    buscarHistoricoAnalises,
  } = useIAAnaliseContext();

  const [podeGerar, setPodeGerar] = useState(true);
  const [mensagemCache, setMensagemCache] = useState("");
  const [carregandoCache, setCarregandoCache] = useState(true);
  const [chatAberto, setChatAberto] = useState(false);
  const [laudoEspecialAberto, setLaudoEspecialAberto] = useState(false);

  // ✅ NOVO: Estados para histórico
  const [historicoAnalises, setHistoricoAnalises] = useState<AnaliseIA[]>([]);
  const [analiseVisualizada, setAnaliseVisualizada] =
    useState<AnaliseIA | null>(null);
  const [mostrarHistorico, setMostrarHistorico] = useState(false);
  // ⚠️ NOVO: Estado para exames de imagem sem análise
  const [examesSemAnalise, setExamesSemAnalise] = useState<number>(0);
  // 🎯 Estados para calibração de semáforos
  const [calibracaoVoto, setCalibracaoVoto] = useState<string | null>(null);
  const [calibracaoObs, setCalibracaoObs] = useState("");
  // B227 (AI_114): categoria do erro apontado (só quando Parcial/Discordo).
  const [calibracaoCategoria, setCalibracaoCategoria] = useState<string | null>(
    null
  );
  const [calibracaoSalva, setCalibracaoSalva] = useState(false);
  const [salvandoCalibracao, setSalvandoCalibracao] = useState(false);
  const [totalExamesImagem, setTotalExamesImagem] = useState<number>(0);
  const { usuarioLogado, firebaseUser } = useAuth();
  // 🔐 B88 (AI_98): nome legado - hoje significa "somente leitura".
  // Cobre convidado E observador (espelho de canWriteClinical das rules).
  const isConvidado = !podeEscreverClinico(usuarioLogado);

  // ⚠️ NOVO: Verificar exames de imagem sem análise
  const verificarExamesSemAnalise = async () => {
    try {
      const imagensRef = collection(db, "exames-imagem");
      const q = query(imagensRef, where("pacienteId", "==", paciente.id));
      const snapshot = await getDocs(q);

      const exames = snapshot.docs.map((doc) => doc.data());
      const semAnalise = exames.filter(
        (e) => !e.achados && !e.analiseCompleta && !e.apenasTexto
      ).length;

      setTotalExamesImagem(exames.length);
      setExamesSemAnalise(semAnalise);
    } catch (error) {
      console.warn("⚠️ Erro ao verificar exames de imagem:", error);
    }
  };
  // Verificar cache e carregar histórico ao montar componente
  useEffect(() => {
    const verificar = async () => {
      setCarregandoCache(true);

      // Limpar estado anterior ao trocar de paciente
      setAnaliseVisualizada(null);
      setHistoricoAnalises([]);

      // Buscar última análise
      const ultima = await buscarUltimaAnalise(paciente.id);
      if (ultima) {
        setAnaliseVisualizada(ultima);
      }

      // ✅ NOVO: Buscar histórico de análises
      const historico = await buscarHistoricoAnalises(paciente.id, 10);
      setHistoricoAnalises(historico);

      // Verificar se pode gerar nova
      // ⚠️ DESATIVADO TEMPORARIAMENTE PARA DESENVOLVIMENTO
      // const cache = await verificarCache(paciente.id);
      // setPodeGerar(cache.podeGerar);
      // if (!cache.podeGerar) {
      //   setMensagemCache(cache.motivoBloqueio || "Aguarde para nova análise");
      // }
      setPodeGerar(true); // Sempre permite em modo dev

      // ⚠️ NOVO: Verificar exames de imagem sem análise
      await verificarExamesSemAnalise();

      setCarregandoCache(false);
    };

    verificar();
  }, [paciente.id]);

  // Atualizar análise visualizada quando analiseAtual mudar
  useEffect(() => {
    if (analiseAtual && analiseAtual.pacienteId === paciente.id) {
      setAnaliseVisualizada(analiseAtual);
    }
  }, [analiseAtual, paciente.id]);

  // 🎯 Carregar calibração existente ou resetar ao trocar de análise
  useEffect(() => {
    const carregarCalibracao = async () => {
      if (!analiseVisualizada?.id) return;
      setCalibracaoVoto(null);
      setCalibracaoObs("");
      setCalibracaoCategoria(null);
      setCalibracaoSalva(false);
      try {
        const q = query(
          collection(db, "calibracoes-semaforo"),
          where("analiseId", "==", analiseVisualizada.id)
        );
        const snap = await getDocs(q);
        if (!snap.empty) {
          const dados = snap.docs[0].data();
          setCalibracaoVoto(dados.voto);
          setCalibracaoObs(dados.observacao || "");
          setCalibracaoCategoria(dados.categoriaErro || null);
          setCalibracaoSalva(true);
        }
      } catch (err) {
        console.error("Erro ao buscar calibração:", err);
      }
    };
    carregarCalibracao();
  }, [analiseVisualizada?.id]);

  // Handler para gerar análise
  const handleGerarAnalise = async () => {
    await gerarAnalise(paciente);
    // Recarregar histórico
    const historico = await buscarHistoricoAnalises(paciente.id, 10);
    setHistoricoAnalises(historico);
  };

  // 🎯 Salvar calibração no Firebase
  const salvarCalibracao = async () => {
    if (!calibracaoVoto || !analiseVisualizada || !paciente) return;
    setSalvandoCalibracao(true);
    try {
      await addDoc(collection(db, "calibracoes-semaforo"), {
        analiseId: analiseVisualizada.id,
        pacienteId: paciente.id,
        voto: calibracaoVoto,
        observacao: calibracaoObs.trim() || null,
        // B227 (AI_114): categoria do erro só faz sentido quando NÃO é "concordo".
        categoriaErro:
          calibracaoVoto !== "concordo" ? calibracaoCategoria || null : null,
        dataAnalise: analiseVisualizada.dataAnalise,
        timestamp: Timestamp.now(),
      });
      // MECR-RAG: marcar análise como aprovada para uso como few-shot.
      // B222 (AI_102): só "Concordo SEM ressalva" vira exemplo-ouro. Um
      // "Concordo, MAS <observação>" carrega uma correção do médico — usá-lo
      // como exemplo limpo faria o MECR PROPAGAR o erro apontado. Com
      // observação, o sinal fica registrado em `calibracoes-semaforo` (acima),
      // mas a análise NÃO entra no few-shot (não recebe aprovada:true).
      if (
        calibracaoVoto === "concordo" &&
        !calibracaoObs.trim() &&
        analiseVisualizada.id
      ) {
        // 🔐 B142.1 — Registro de autoria da aprovação (CFM 2454/2026 Art. 4.V)
        // A análise só é marcada como aprovada se houver médico autenticado.
        // Defesa em profundidade: em produção sempre há médico logado para
        // chegar até aqui, mas o guard blinda contra bug futuro.
        if (!firebaseUser) {
          console.warn(
            "⚠️ MECR-RAG: aprovação ignorada — médico não autenticado (CFM 2454/2026 Art. 4.V)"
          );
        } else if (!modalidadeUsaFewShotMECR(paciente.tipoAtendimento)) {
          // 🔒 B351 passo 5b (AI_153) — a MARCA de aprovação não é gravada no
          // doador. O voto do médico continua registrado em
          // `calibracoes-semaforo` (acima) — o sinal de qualidade não se perde;
          // o que não acontece é a análise virar exemplo few-shot.
          // Motivo em useIAAnalise.buscarAnalisesAprovadas: o texto do doador
          // carrega o conjunto mínimo de um RECEPTOR-terceiro, e enquanto a
          // modalidade não tiver análise própria uma aprovação congelaria o
          // enquadramento do receptor como exemplo-ouro. Decisão de Leonardo
          // (05/09), a reabrir com o advogado.
          console.log(
            "🔒 MECR-RAG: aprovação não marcada (doador de fígado — B351); calibração registrada normalmente"
          );
        } else {
          try {
            await updateDoc(doc(db, "analises-ia", analiseVisualizada.id), {
              aprovada: true,
              tipoAtendimentoAprovado:
                paciente.tipoAtendimento || "nao-especificado",
              // 🔒 B171/M-R5 — minimizar o diagnóstico (texto livre) antes de ele
              // virar exemplo few-shot na análise de outro paciente.
              diagnosticoBaseAprovado: minimizarTextoMECR(
                (paciente as any).inscricaoTx?.diagnosticoBase ||
                  (paciente as any).diagnosticoBase ||
                  "",
                paciente.nome ? [paciente.nome] : []
              ),
              // 🔐 B142.1 — autoria
              aprovadoPorUid: firebaseUser.uid,
              aprovadoPorEmail: firebaseUser.email ?? null,
              aprovadoEm: new Date().toISOString(),
            });
            console.log(
              "✅ MECR-RAG: Análise marcada como aprovada por:",
              firebaseUser.email
            );
          } catch (mecrErr) {
            console.warn(
              "⚠️ MECR-RAG: Erro ao marcar aprovação (não-crítico):",
              mecrErr
            );
          }
        }
      }
      setCalibracaoSalva(true);
    } catch (err) {
      console.error("Erro ao salvar calibração:", err);
    } finally {
      setSalvandoCalibracao(false);
    }
  };
  // Formatar data
  const formatarData = (isoDate: string) => {
    const data = new Date(isoDate);
    return data.toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Cor do status
  const getCorStatus = (status: string) => {
    switch (status) {
      case "estavel":
        return "#10b981";
      case "atencao":
        return "#f59e0b";
      case "critico":
        return "#ef4444";
      default:
        return theme.primary[600];
    }
  };

  // Cor da prioridade
  const getCorPrioridade = (prioridade: string) => {
    switch (prioridade) {
      case "alta":
        return "#ef4444";
      case "media":
        return "#f59e0b";
      case "baixa":
        return "#10b981";
      default:
        return theme.gray[600];
    }
  };

  // 🔒 [B206, AI_94] Renderização inline SEGURA — substitui os 3 usos de
  // dangerouslySetInnerHTML (achado S5 da auditoria AI_92, risco XSS):
  // **negrito** e emojis sinalizadores viram elementos React; o texto
  // restante é escapado pelo próprio React, então HTML embutido na saída
  // do modelo (ou citado de texto livre do prontuário) NUNCA executa.
  // Visual idêntico ao anterior (mesmas cores e pesos).
  const CORES_EMOJI: Record<string, string> = {
    "✅": "#10b981",
    "⚠️": "#f59e0b",
    "🚨": "#ef4444",
    "❌": "#ef4444",
    "⚡": "#f59e0b",
  };

  const renderizarInlineSeguro = (texto: string): React.ReactNode[] =>
    texto
      .split(/(\*\*[^*]+\*\*|✅|⚠️|🚨|❌|⚡)/g)
      .filter(Boolean)
      .map((parte, i) => {
        if (/^\*\*[^*]+\*\*$/.test(parte)) {
          return <strong key={i}>{parte.slice(2, -2)}</strong>;
        }
        const cor = CORES_EMOJI[parte];
        if (cor) {
          return (
            <span key={i} style={{ color: cor }}>
              {parte}
            </span>
          );
        }
        return parte;
      });

  // ✅ Função para formatar texto com Markdown simples
  // 🧱 B374 (AI_156) — a análise geral não desenhava TABELA. O modelo emite
  // tabela markdown de verdade (cabeçalho + "|---|---|" + linhas) no fim das
  // recomendações, e esta tela, que tem renderizador próprio, cuspia os canos
  // crus — enquanto as análises setoriais desenhavam certo desde o B305.
  // Achado por Leonardo na leitura cega do B363 (30/10).
  // Desenho: separa o texto em partes (reusando `separarPartes`, já testado) e
  // só o que for tabela vai para `TabelaMarkdown`. SEM tabela, o caminho é
  // byte-idêntico ao de antes — zero regressão no formato que já funcionava.
  const formatarTextoAnalise = (texto: string): React.ReactNode[] => {
    const partes = separarPartes(texto || "");
    if (partes.length === 1 && partes[0].tipo === "texto") {
      return formatarBlocoDeTexto(texto);
    }
    const saida: React.ReactNode[] = [];
    partes.forEach((parte, i) => {
      if (parte.tipo === "tabela") {
        saida.push(
          <TabelaMarkdown key={`tb-${i}`} linhas={parte.linhas} chave={`g${i}`} />
        );
      } else {
        saida.push(...formatarBlocoDeTexto(parte.texto));
      }
    });
    return saida;
  };

  const formatarBlocoDeTexto = (texto: string) => {
    // 🩹 Normaliza texto CORRIDO: o modelo às vezes devolve a análise sem
    // quebras de linha (→ "parede de texto"). Quando há poucas quebras, insere
    // uma quebra de parágrafo antes de cada item numerado ("1. ", "2. " ...) —
    // que estruturam tanto as seções (1. RESUMO, 2. ANÁLISE) quanto as
    // recomendações. Em pt-BR os decimais usam vírgula, então "\d+. " só casa
    // com itens de lista (não com valores como 1,14 ou datas).
    const poucasQuebras = (texto.match(/\n/g) || []).length < 3;
    const textoBase = poucasQuebras
      ? texto.replace(/(\S)\s+(\d{1,2}\.\s)/g, "$1\n\n$2")
      : texto;
    // 🩹 AI_115: o modelo às vezes quebra o "N." e o TÍTULO em linhas separadas
    // ("1.\nRESUMO CLÍNICO"). Junta o número órfão ao título em MAIÚSCULAS da
    // linha seguinte, para o título voltar a ser reconhecido (e negritado).
    const textoNorm = textoBase.replace(
      /(^|\n)[ \t]*(\d{1,2})\.[ \t]*\n+[ \t]*([A-ZÀ-Ý][A-ZÀ-Ý0-9 ()/.,:–—-]+?)[ \t]*(?=\n|$)/g,
      "$1$2. $3"
    );
    // Divide o texto em linhas
    const linhas = textoNorm.split(/\n/);

    const elementos: JSX.Element[] = [];
    let chaveAtual = 0;

    // 🅰️ AI_115: título de SEÇÃO = linha toda em MAIÚSCULAS (aceita "N." na
    // frente) — ex.: "RESUMO CLÍNICO", "1. ANÁLISE DETALHADA". Distingue das
    // recomendações numeradas (que têm minúsculas). Restaura o negrito perdido:
    // antes "1. TÍTULO" caía no ramo de item numerado e só o número era negrito.
    const ehTituloSecao = (l: string): boolean => {
      const semNum = l.replace(/^\d{1,2}\.\s*/, "").trim();
      return (
        semNum.length >= 3 &&
        semNum.length <= 60 &&
        /[A-ZÀ-Ý]/.test(semNum) &&
        !/[a-zà-ÿ]/.test(semNum)
      );
    };
    // B316-FU: o cabeçalho do bloco de situação especial vem em CAIXA ALTA, mas
    // termina com "(candidata):" em minúsculas → não casa ehTituloSecao. Detectá-lo
    // à parte para virar título azul. Prefixo específico = seguro (não é prosa).
    const ehTituloSituacaoEspecial = (l: string): boolean =>
      /^situa[çc][ãa]o especial\b/i.test(l) && l.length <= 90;

    linhas.forEach((linha) => {
      const linhaLimpa = linha.trim();

      // Ignora linhas vazias ou apenas com # ou -
      if (
        !linhaLimpa ||
        linhaLimpa === "#" ||
        linhaLimpa === "-" ||
        linhaLimpa === "•"
      )
        return;

      // Título principal (# )
      if (linhaLimpa.match(/^#{1}\s+\d*\.?\s*/)) {
        elementos.push(
          <h2
            key={chaveAtual++}
            style={{
              fontSize: "20px",
              fontWeight: "bold",
              color: theme.primary[700],
              marginTop: elementos.length > 0 ? "24px" : "0",
              marginBottom: "12px",
              paddingBottom: "8px",
              borderBottom: `2px solid ${theme.primary[200]}`,
            }}
          >
            {linhaLimpa.replace(/^#+\s*/, "").replace(/\*\*/g, "")}
          </h2>
        );
      }
      // Subtítulo (## )
      else if (linhaLimpa.match(/^#{2}\s+/)) {
        elementos.push(
          <h3
            key={chaveAtual++}
            style={{
              fontSize: "17px",
              fontWeight: "600",
              color: theme.gray[800],
              marginTop: "20px",
              marginBottom: "10px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <span
              style={{
                width: "4px",
                height: "20px",
                backgroundColor: theme.primary[500],
                borderRadius: "2px",
              }}
            />
            {linhaLimpa.replace(/^#+\s*/, "").replace(/\*\*/g, "")}
          </h3>
        );
      }
      // Subseção (### )
      else if (linhaLimpa.match(/^#{3,}\s+/)) {
        elementos.push(
          <h4
            key={chaveAtual++}
            style={{
              fontSize: "15px",
              fontWeight: "600",
              color: theme.gray[700],
              marginTop: "16px",
              marginBottom: "8px",
            }}
          >
            {linhaLimpa.replace(/^#+\s*/, "").replace(/\*\*/g, "")}
          </h4>
        );
      }
      // 🅰️ Título de SEÇÃO em MAIÚSCULAS (com ou sem "N.") — negrito (AI_115)
      // + B316-FU: cabeçalho do bloco de situação especial (mesmo estilo azul).
      else if (ehTituloSecao(linhaLimpa) || ehTituloSituacaoEspecial(linhaLimpa)) {
        const num = linhaLimpa.match(/^(\d{1,2})\./)?.[1];
        const titulo = linhaLimpa.replace(/^\d{1,2}\.\s*/, "").trim();
        elementos.push(
          <h3
            key={chaveAtual++}
            style={{
              fontSize: "16px",
              fontWeight: "bold",
              color: theme.primary[700],
              marginTop: elementos.length > 0 ? "20px" : "0",
              marginBottom: "10px",
              display: "flex",
              alignItems: "baseline",
              gap: "8px",
            }}
          >
            {num && <span style={{ color: theme.primary[600] }}>{num}.</span>}
            <span>{titulo}</span>
          </h3>
        );
      }
      // Item de lista numerado (1. 2. etc)
      else if (linhaLimpa.match(/^\d+\.\s+/)) {
        elementos.push(
          <div
            key={chaveAtual++}
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: "8px",
              marginBottom: "8px",
              paddingLeft: "8px",
            }}
          >
            <span
              style={{
                color: theme.primary[600],
                fontWeight: "600",
                minWidth: "20px",
              }}
            >
              {linhaLimpa.match(/^\d+/)?.[0]}.
            </span>
            <span style={{ fontSize: "14px", color: theme.gray[700], flex: 1 }}>
              {renderizarInlineSeguro(linhaLimpa.replace(/^\d+\.\s*/, ""))}
            </span>
          </div>
        );
      }
      // Item de lista (- ou • ou *)
      else if (linhaLimpa.match(/^[-•*]\s+/)) {
        elementos.push(
          <div
            key={chaveAtual++}
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: "8px",
              marginBottom: "6px",
              paddingLeft: "16px",
            }}
          >
            <span style={{ color: theme.primary[500], fontWeight: "bold" }}>
              •
            </span>
            <span style={{ fontSize: "14px", color: theme.gray[700], flex: 1 }}>
              {renderizarInlineSeguro(linhaLimpa.replace(/^[-•*]\s*/, ""))}
            </span>
          </div>
        );
      }
      // Separador (---)
      else if (linhaLimpa === "---") {
        elementos.push(
          <hr
            key={chaveAtual++}
            style={{
              border: "none",
              borderTop: `1px solid ${theme.gray[200]}`,
              margin: "16px 0",
            }}
          />
        );
      }
      // Parágrafo normal
      else {
        elementos.push(
          <p
            key={chaveAtual++}
            style={{
              fontSize: "14px",
              color: theme.gray[700],
              marginBottom: "8px",
              lineHeight: "1.6",
            }}
          >
            {renderizarInlineSeguro(linhaLimpa)}
          </p>
        );
      }
    });

    return elementos;
  };

  // ✅ NOVO: Verifica se o texto tem formatação Markdown
  const temFormatacaoMarkdown = (texto: string) => {
    return (
      texto.includes("# ") ||
      texto.includes("## ") ||
      texto.includes("### ") ||
      texto.includes("**") ||
      texto.includes("- ") ||
      // Bloco único e longo = a análise inteira (nunca é um "achado" de lista).
      // Roteia para o renderizador formatado mesmo quando o modelo devolve o
      // texto CORRIDO, sem markdown — corrige a "parede de texto" intermitente.
      texto.length > 300
    );
  };

  // ✅ NOVO: Selecionar análise do histórico
  const selecionarAnalise = (analise: AnaliseIA) => {
    setAnaliseVisualizada(analise);
    setMostrarHistorico(false);
  };
  // ✅ Contar achados da análise
  const contarAchadosAnalise = (analise: AnaliseIA): number => {
    // Se tem achados estruturados
    const achados = analise.resumo?.principaisAchados || [];
    if (achados.length > 1) return achados.length;

    // Se é texto único, contar problemas ativos + em investigação
    const texto = achados[0] || "";

    // 🫀 B351 passo 5b (AI_153) — no DOADOR o que conta como "achado" são os
    // alertas marcados, não as seções. Sem isto o contador casaria o padrão
    // "N. TÍTULO" das sete seções numeradas e exibiria "7 achados" em toda
    // análise, inclusive numa sem nenhum alerta.
    if (texto.includes("ALERTAS PARA A DOAÇÃO")) {
      const marcados = texto.match(/^[ \t]*(❌|⚠️)/gm) || [];
      return marcados.length;
    }

    if (texto.length > 100) {
      let count = 0;
      // Contar bullets (•) na seção de problemas ativos e em investigação
      const posProblemas = texto.search(/Problemas ativos|Em investigação/i);
      if (posProblemas > -1) {
        const secao = texto.substring(posProblemas, posProblemas + 2000);
        const bullets = secao.match(/[•\-]\s+\S/g) || [];
        count = bullets.length;
      }
      if (count > 0) return count;

      // Fallback: contar seções principais (1. RESUMO, 2. ANÁLISE, etc.)
      const secoes = texto.match(/\d+\.\s+[A-ZÁÉÍÓÚÃÕÇ]{3,}/g) || [];
      return secoes.length || 1;
    }

    return achados.length || 1;
  };

  const contarSugestoesAnalise = (analise: AnaliseIA): number => {
    if (analise.sugestoes && analise.sugestoes.length > 0) {
      return analise.sugestoes.length;
    }

    const texto = analise.resumo?.principaisAchados?.[0] || "";
    if (texto.length > 100) {
      const posRecomendacoes = texto.search(/RECOMENDAÇÕES/i);
      if (posRecomendacoes > -1) {
        const textoApos = texto.substring(posRecomendacoes);
        const fimSecao = textoApos.search(/(?:CONSIDERAÇÕES|Urgência\s*:)/i);
        const secao =
          fimSecao > -1 ? textoApos.substring(0, fimSecao) : textoApos;

        // Contar itens numerados: "1." ou "1. " seguido de qualquer texto
        // Permitindo quebra de linha entre número e texto
        const itensNumerados = secao.match(/(?:^|\n)\s*\d+\.\s/g) || [];

        // Contar bullet points: "•" ou "-" como item de lista
        const itensBullet = secao.match(/(?:^|\n)\s*[•\-]\s/g) || [];

        // Excluir títulos de seção (ex: "4. RECOMENDAÇÕES")
        const titulosSecao =
          /RECOMENDAÇÕES|RESUMO|ANÁLISE|AVALIAÇÃO|CONSIDERAÇÕES|MONITORAMENTO|AJUSTES/i;
        const numeradosFiltrados = itensNumerados.filter((item) => {
          const pos = secao.indexOf(item);
          const proximosChars = secao.substring(pos, pos + 50);
          return !titulosSecao.test(proximosChars);
        });

        const total = numeradosFiltrados.length + itensBullet.length;
        return Math.min(total, 20);
      }
    }

    return 0;
  };

  return (
    <div style={{ padding: "24px" }}>
      {/* CSS para animações */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes progressPulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.6; }
        }
      `}</style>

      {/* CABEÇALHO */}
      <div
        style={{
          marginBottom: "24px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "16px",
        }}
      >
        <div>
          <h2
            style={{
              fontSize: "24px",
              fontWeight: "bold",
              color: theme.gray[900],
              marginBottom: "8px",
              fontFamily: theme.fonts.sans,
            }}
          >
            <Bot size={20} style={{ marginRight: "8px" }} /> Análise Clínica por
            IA
          </h2>
          <p
            style={{
              fontSize: "14px",
              color: theme.gray[600],
              fontFamily: theme.fonts.sans,
            }}
          >
            Análise integrada: consultas, exames, marcadores, imagens e condutas
          </p>
        </div>

        <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
          {/* ✅ NOVO: Botão histórico */}
          {historicoAnalises.length > 1 && (
            <button
              onClick={() => setMostrarHistorico(!mostrarHistorico)}
              style={{
                padding: "10px 16px",
                backgroundColor: mostrarHistorico
                  ? theme.primary[100]
                  : theme.gray[100],
                color: mostrarHistorico ? theme.primary[700] : theme.gray[700],
                border: `1px solid ${
                  mostrarHistorico ? theme.primary[300] : theme.gray[300]
                }`,
                borderRadius: "8px",
                fontSize: "14px",
                fontWeight: "500",
                cursor: "pointer",
                fontFamily: theme.fonts.sans,
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              <ScrollText size={14} style={{ marginRight: "6px" }} /> Histórico
              ({historicoAnalises.length})
            </button>
          )}

          {/* ⚠️ ALERTA: Exames de imagem sem análise */}
          {examesSemAnalise > 0 && (
            <div
              style={{
                padding: "10px 16px",
                backgroundColor: "#fef3c7",
                border: "1px solid #f59e0b",
                borderRadius: "8px",
                fontSize: "13px",
                color: "#92400e",
                fontFamily: theme.fonts.sans,
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <AlertTriangle size={14} style={{ marginRight: "4px" }} />{" "}
              {examesSemAnalise} de {totalExamesImagem} exame(s) de imagem sem
              análise de IA.
              <span style={{ fontWeight: "600" }}>
                Analise-os primeiro para uma avaliação mais completa.
              </span>
            </div>
          )}

          {/* BOTÃO DISCUTIR CASO — §6.3.2 (AI_145): a equipe (não-médico) PODE abrir
              o chat para LER as discussões anteriores; a GERAÇÃO de novas mensagens
              é médico-only (o input+Enviar do ChatDiscussaoCaso é gateado por
              ehMedico — não-médico vê só o histórico, sem caixa de digitar).
              Decisão de Leonardo (validação ao vivo): manter a leitura do chat. */}
          {!isConvidado && (
            <button
              onClick={() => setChatAberto(true)}
              style={{
                padding: "12px 24px",
                backgroundColor: theme.primary[100],
                color: theme.primary[700],
                border: `2px solid ${theme.primary[300]}`,
                borderRadius: "8px",
                fontSize: "14px",
                fontWeight: "600",
                cursor: "pointer",
                transition: "all 0.2s",
                fontFamily: theme.fonts.sans,
              }}
            >
              <MessageCircle size={14} style={{ marginRight: "6px" }} />{" "}
              Discutir Caso
            </button>
          )}

          {/* BOTÃO LAUDO ESPECIAL — B250 (AI_113): relatórios complexos/sob
              medida (judicial/MP, alto custo, 2ª opinião). Ato privativo do
              médico (espelha o gate B243 no /api/analyze). */}
          {!isConvidado && ehMedico(usuarioLogado) && (
            <button
              onClick={() => setLaudoEspecialAberto(true)}
              style={{
                padding: "12px 24px",
                backgroundColor: theme.primary[100],
                color: theme.primary[700],
                border: `2px solid ${theme.primary[300]}`,
                borderRadius: "8px",
                fontSize: "14px",
                fontWeight: "600",
                cursor: "pointer",
                transition: "all 0.2s",
                fontFamily: theme.fonts.sans,
              }}
            >
              <Scale size={14} style={{ marginRight: "6px" }} /> Laudo Especial
            </button>
          )}

          {/* BOTÃO GERAR ANÁLISE — 🔐 B88: ato privativo do médico
              (espelho das firestore.rules e do gate no /api/analyze) */}
          {!isConvidado && ehMedico(usuarioLogado) && (
            <button
              onClick={handleGerarAnalise}
              disabled={loading || !podeGerar || carregandoCache}
              style={{
                padding: "12px 24px",
                backgroundColor:
                  loading || !podeGerar ? theme.gray[300] : theme.primary[600],
                color: "white",
                border: "none",
                borderRadius: "8px",
                fontSize: "14px",
                fontWeight: "600",
                cursor: loading || !podeGerar ? "not-allowed" : "pointer",
                opacity: loading || !podeGerar ? 0.6 : 1,
                transition: "all 0.2s",
                fontFamily: theme.fonts.sans,
              }}
            >
              {loading ? (
                <>
                  <RefreshCw size={14} style={{ marginRight: "6px" }} />{" "}
                  Analisando...
                </>
              ) : carregandoCache ? (
                <>
                  <Clock size={14} style={{ marginRight: "6px" }} />{" "}
                  Verificando...
                </>
              ) : (
                <>
                  <Sparkles size={14} style={{ marginRight: "6px" }} /> Gerar
                  Nova Análise
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* ✅ NOVO: PAINEL DE HISTÓRICO */}
      {mostrarHistorico && historicoAnalises.length > 0 && (
        <div
          style={{
            backgroundColor: "white",
            border: `1px solid ${theme.gray[200]}`,
            borderRadius: "12px",
            padding: "16px",
            marginBottom: "24px",
            fontFamily: theme.fonts.sans,
          }}
        >
          <h3
            style={{
              fontSize: "16px",
              fontWeight: "600",
              color: theme.gray[800],
              marginBottom: "12px",
            }}
          >
            <ScrollText size={14} style={{ marginRight: "6px" }} /> Histórico de
            Análises
          </h3>
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {historicoAnalises.map((analise, index) => (
              <div
                key={analise.id}
                onClick={() => selecionarAnalise(analise)}
                style={{
                  padding: "12px 16px",
                  backgroundColor:
                    analiseVisualizada?.id === analise.id
                      ? theme.primary[50]
                      : theme.gray[50],
                  border: `1px solid ${
                    analiseVisualizada?.id === analise.id
                      ? theme.primary[300]
                      : theme.gray[200]
                  }`,
                  borderRadius: "8px",
                  cursor: "pointer",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  transition: "all 0.2s",
                }}
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "12px" }}
                >
                  <span
                    style={{
                      width: "10px",
                      height: "10px",
                      borderRadius: "50%",
                      backgroundColor: getCorStatus(analise.resumo.statusGeral),
                    }}
                  />
                  <div>
                    <p
                      style={{
                        fontSize: "14px",
                        fontWeight: "500",
                        color: theme.gray[800],
                        margin: 0,
                      }}
                    >
                      {formatarData(analise.dataAnalise)}
                      {index === 0 && (
                        <span
                          style={{
                            marginLeft: "8px",
                            fontSize: "11px",
                            backgroundColor: theme.primary[100],
                            color: theme.primary[700],
                            padding: "2px 8px",
                            borderRadius: "10px",
                          }}
                        >
                          Mais recente
                        </span>
                      )}
                    </p>
                    <p
                      style={{
                        fontSize: "12px",
                        color: theme.gray[500],
                        margin: 0,
                      }}
                    >
                      Status: {analise.resumo.statusGeral} •{" "}
                      {contarAchadosAnalise(analise)} achados •{" "}
                      {contarSugestoesAnalise(analise)} sugestões
                    </p>
                  </div>
                </div>
                <span style={{ fontSize: "18px", color: theme.gray[400] }}>
                  →
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* MENSAGEM DE CACHE */}
      {!podeGerar && !loading && (
        <div
          style={{
            padding: "16px",
            backgroundColor: "#fef3c7",
            border: "1px solid #fbbf24",
            borderRadius: "8px",
            marginBottom: "24px",
            fontFamily: theme.fonts.sans,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Clock size={20} color="#92400e" />
            <span style={{ fontSize: "14px", color: "#92400e" }}>
              {mensagemCache}
            </span>
          </div>
        </div>
      )}

      {/* MENSAGEM DE ERRO */}
      {erro && (
        <div
          style={{
            padding: "16px",
            backgroundColor: "#fee2e2",
            border: "1px solid #ef4444",
            borderRadius: "8px",
            marginBottom: "24px",
            fontFamily: theme.fonts.sans,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <X size={20} color="#991b1b" />
            <span style={{ fontSize: "14px", color: "#991b1b" }}>{erro}</span>
          </div>
        </div>
      )}

      {/* BARRA DE PROGRESSO FLUTUANTE — não bloqueia a tela */}
      {loading && (
        <div
          style={{
            padding: "16px 20px",
            backgroundColor: theme.primary[50],
            border: `1px solid ${theme.primary[300]}`,
            borderRadius: "12px",
            marginBottom: "24px",
            fontFamily: theme.fonts.sans,
            display: "flex",
            alignItems: "center",
            gap: "16px",
            position: "sticky",
            top: "0",
            zIndex: 10,
            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
          }}
        >
          <div
            style={{
              width: "36px",
              height: "36px",
              borderRadius: "50%",
              backgroundColor: theme.primary[100],
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <RefreshCw
              size={18}
              color={theme.primary[600]}
              style={{ animation: "spin 1.5s linear infinite" }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <p
              style={{
                fontSize: "14px",
                fontWeight: "600",
                color: theme.primary[800],
                margin: 0,
              }}
            >
              Análise em andamento...
            </p>
            <p
              style={{
                fontSize: "13px",
                color: theme.primary[600],
                margin: "2px 0 0 0",
              }}
            >
              {progresso || "Preparando..."}
            </p>
          </div>
          <div
            style={{
              width: "160px",
              height: "6px",
              backgroundColor: theme.primary[200],
              borderRadius: "3px",
              overflow: "hidden",
              flexShrink: 0,
            }}
          >
            <div
              style={{
                height: "100%",
                backgroundColor: theme.primary[500],
                borderRadius: "3px",
                animation: "progressPulse 2s ease-in-out infinite",
                width: progresso.includes("Etapa 2")
                  ? "75%"
                  : progresso.includes("Etapa 1")
                  ? "40%"
                  : progresso.includes("Salvando")
                  ? "95%"
                  : "15%",
                transition: "width 0.5s ease",
              }}
            />
          </div>
        </div>
      )}

      {/* 🌊 B389 (AI_157) — RASCUNHO DA ETAPA 1, enquanto o modelo escreve.
          Mesmo formatador do texto final (títulos, tabelas B374). Visual
          provisório (tracejado) + aviso: a Etapa 2 corrige dados; o final
          substitui. Some sozinho quando `rascunho` esvazia. */}
      {loading && rascunho && (
        <div
          style={{
            marginBottom: "24px",
            padding: "20px 24px",
            border: `2px dashed ${theme.primary[300]}`,
            borderRadius: "12px",
            backgroundColor: "#fdfdfc",
            fontFamily: theme.fonts.sans,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              marginBottom: "12px",
              fontSize: "13px",
              color: theme.primary[700],
              fontWeight: 600,
            }}
          >
            <RefreshCw
              size={14}
              style={{ animation: "spin 1.5s linear infinite" }}
            />
            {progresso.includes("Etapa 2")
              ? "Rascunho da Etapa 1 — em revisão (Etapa 2/2). O texto final substitui este."
              : "Rascunho — a análise está sendo escrita. A revisão pode corrigir dados."}
          </div>
          <div style={{ fontSize: "14px", lineHeight: 1.6, color: theme.gray[800] }}>
            {formatarTextoAnalise(rascunho)}
          </div>
        </div>
      )}

      {/* RESULTADO DA ANÁLISE — visível mesmo durante loading (mostra análise anterior) */}
      {analiseVisualizada && (
        <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
          {/* ✅ INDICADOR DE ANÁLISE HISTÓRICA */}
          {analiseVisualizada.id !== analiseAtual?.id &&
            historicoAnalises.length > 1 && (
              <div
                style={{
                  padding: "12px 16px",
                  backgroundColor: "#e0f2fe",
                  border: "1px solid #0ea5e9",
                  borderRadius: "8px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  fontFamily: theme.fonts.sans,
                }}
              >
                <span style={{ fontSize: "14px", color: "#0369a1" }}>
                  <ScrollText size={14} style={{ marginRight: "6px" }} />{" "}
                  Visualizando análise de{" "}
                  <strong>
                    {formatarData(analiseVisualizada.dataAnalise)}
                  </strong>
                </span>
                <button
                  onClick={() => setAnaliseVisualizada(historicoAnalises[0])}
                  style={{
                    padding: "6px 12px",
                    backgroundColor: "#0ea5e9",
                    color: "white",
                    border: "none",
                    borderRadius: "6px",
                    fontSize: "12px",
                    fontWeight: "600",
                    cursor: "pointer",
                  }}
                >
                  Ver mais recente
                </button>
              </div>
            )}

          {/* B327 (AI_147) — delta "o que mudou desde a análise anterior".
              Só aparece quando existe uma análise anterior a esta (referência).
              Determinístico, sem IA, sem tocar a geração. */}
          {(() => {
            const idx = historicoAnalises.findIndex(
              (a) => a.id === analiseVisualizada.id
            );
            const anterior = idx >= 0 ? historicoAnalises[idx + 1] : undefined;
            return anterior?.dataAnalise ? (
              <PainelDelta paciente={paciente} analiseAnterior={anterior} />
            ) : null;
          })()}

          {/* RESUMO EXECUTIVO */}
          <div
            style={{
              backgroundColor: "white",
              border: `2px solid ${getCorStatus(
                analiseVisualizada.resumo.statusGeral
              )}`,
              borderRadius: "12px",
              padding: "24px",
              fontFamily: theme.fonts.sans,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "16px",
              }}
            >
              <h3
                style={{
                  fontSize: "18px",
                  fontWeight: "bold",
                  color: theme.gray[900],
                }}
              >
                <BarChart2 size={16} style={{ marginRight: "8px" }} /> Resumo
                Executivo
              </h3>
              <SelosStatus
                gravidade={analiseVisualizada.resumo.gravidade}
                urgencia={analiseVisualizada.resumo.urgencia}
                adequabilidade={analiseVisualizada.resumo.adequabilidade}
                statusGeral={analiseVisualizada.resumo.statusGeral}
              />
            </div>

            <div style={{ marginBottom: "16px" }}>
              <p
                style={{
                  fontSize: "12px",
                  color: theme.gray[500],
                  marginBottom: "4px",
                }}
              >
                Análise realizada em:
              </p>
              <p
                style={{
                  fontSize: "14px",
                  fontWeight: "600",
                  color: theme.gray[700],
                }}
              >
                {formatarData(analiseVisualizada.dataAnalise)}
              </p>
            </div>

            <div>
              <p
                style={{
                  fontSize: "14px",
                  fontWeight: "600",
                  color: theme.gray[700],
                  marginBottom: "12px",
                }}
              >
                Principais Achados:
              </p>

              {/* ✅ NOVO: Verifica se é texto longo com Markdown ou lista simples */}
              {analiseVisualizada.resumo.principaisAchados.length === 1 &&
              temFormatacaoMarkdown(
                analiseVisualizada.resumo.principaisAchados[0]
              ) ? (
                // Texto único com formatação Markdown
                <div
                  style={{
                    backgroundColor: theme.gray[50],
                    borderRadius: "8px",
                    padding: "20px",
                    maxHeight: "600px",
                    overflowY: "auto",
                  }}
                >
                  {formatarTextoAnalise(
                    analiseVisualizada.resumo.principaisAchados[0]
                  )}
                </div>
              ) : (
                // Lista simples de achados
                <ul
                  style={{
                    listStyle: "none",
                    padding: 0,
                    margin: 0,
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                  }}
                >
                  {analiseVisualizada.resumo.principaisAchados.map(
                    (achado, index) => (
                      <li
                        key={index}
                        style={{
                          fontSize: "14px",
                          color: theme.gray[800],
                          paddingLeft: "20px",
                          position: "relative",
                        }}
                      >
                        <span
                          style={{
                            position: "absolute",
                            left: 0,
                            color: theme.primary[600],
                          }}
                        >
                          •
                        </span>
                        {achado}
                      </li>
                    )
                  )}
                </ul>
              )}
            </div>
          </div>

          {/* ✅ NOVO: ACHADOS DE IMAGEM (se disponível) */}
          {analiseVisualizada.analises?.imagemRadiologica && (
            <div
              style={{
                backgroundColor: "white",
                border: `2px solid ${theme.primary[400]}`,
                borderRadius: "12px",
                padding: "24px",
                fontFamily: theme.fonts.sans,
              }}
            >
              <h3
                style={{
                  fontSize: "18px",
                  fontWeight: "bold",
                  color: theme.gray[900],
                  marginBottom: "16px",
                }}
              >
                <Camera size={16} style={{ marginRight: "8px" }} /> Achados de
                Imagem
              </h3>

              <div style={{ display: "grid", gap: "16px" }}>
                {/* CHC / Hepatocarcinoma */}
                {analiseVisualizada.analises.imagemRadiologica
                  .hepatocarcinoma && (
                  <div
                    style={{
                      padding: "16px",
                      backgroundColor: analiseVisualizada.analises
                        .imagemRadiologica.hepatocarcinoma.presente
                        ? "#fef2f2"
                        : "#f0fdf4",
                      borderRadius: "8px",
                      border: `1px solid ${
                        analiseVisualizada.analises.imagemRadiologica
                          .hepatocarcinoma.presente
                          ? "#fecaca"
                          : "#bbf7d0"
                      }`,
                    }}
                  >
                    <h4
                      style={{
                        fontSize: "14px",
                        fontWeight: "600",
                        color: analiseVisualizada.analises.imagemRadiologica
                          .hepatocarcinoma.presente
                          ? "#991b1b"
                          : "#166534",
                        marginBottom: "8px",
                      }}
                    >
                      <Target size={14} style={{ marginRight: "6px" }} />{" "}
                      Hepatocarcinoma (CHC):{" "}
                      {analiseVisualizada.analises.imagemRadiologica
                        .hepatocarcinoma.presente
                        ? "PRESENTE"
                        : "Não identificado"}
                    </h4>
                    {analiseVisualizada.analises.imagemRadiologica
                      .hepatocarcinoma.presente && (
                      <>
                        <p
                          style={{
                            fontSize: "13px",
                            color: theme.gray[700],
                            marginBottom: "4px",
                          }}
                        >
                          <strong>Status Milão:</strong>{" "}
                          {
                            analiseVisualizada.analises.imagemRadiologica
                              .hepatocarcinoma.statusMilao
                          }
                        </p>
                        <p style={{ fontSize: "13px", color: theme.gray[700] }}>
                          <strong>Elegibilidade:</strong>{" "}
                          {
                            analiseVisualizada.analises.imagemRadiologica
                              .hepatocarcinoma.elegibilidadeTransplante
                          }
                        </p>
                      </>
                    )}
                  </div>
                )}

                {/* Trombose Portal */}
                {analiseVisualizada.analises.imagemRadiologica
                  .trombosePortal && (
                  <div
                    style={{
                      padding: "16px",
                      backgroundColor: analiseVisualizada.analises
                        .imagemRadiologica.trombosePortal.presente
                        ? "#fffbeb"
                        : "#f0fdf4",
                      borderRadius: "8px",
                      border: `1px solid ${
                        analiseVisualizada.analises.imagemRadiologica
                          .trombosePortal.presente
                          ? "#fde68a"
                          : "#bbf7d0"
                      }`,
                    }}
                  >
                    <h4
                      style={{
                        fontSize: "14px",
                        fontWeight: "600",
                        color: analiseVisualizada.analises.imagemRadiologica
                          .trombosePortal.presente
                          ? "#92400e"
                          : "#166534",
                        marginBottom: "8px",
                      }}
                    >
                      <Droplet
                        size={14}
                        color="#ef4444"
                        style={{ marginRight: "6px" }}
                      />{" "}
                      Trombose Portal:{" "}
                      {analiseVisualizada.analises.imagemRadiologica
                        .trombosePortal.presente
                        ? "PRESENTE"
                        : "Ausente"}
                    </h4>
                    {analiseVisualizada.analises.imagemRadiologica
                      .trombosePortal.presente && (
                      <>
                        <p
                          style={{
                            fontSize: "13px",
                            color: theme.gray[700],
                            marginBottom: "4px",
                          }}
                        >
                          <strong>Classificação Yerdel:</strong>{" "}
                          {
                            analiseVisualizada.analises.imagemRadiologica
                              .trombosePortal.classificacaoYerdel
                          }
                        </p>
                        <p
                          style={{
                            fontSize: "13px",
                            color: theme.gray[700],
                            marginBottom: "4px",
                          }}
                        >
                          <strong>Tipo:</strong>{" "}
                          {
                            analiseVisualizada.analises.imagemRadiologica
                              .trombosePortal.tipo
                          }
                        </p>
                        {analiseVisualizada.analises.imagemRadiologica
                          .trombosePortal.recomendacaoAnticoagulacao && (
                          <p
                            style={{ fontSize: "13px", color: theme.gray[700] }}
                          >
                            <strong>Anticoagulação:</strong>{" "}
                            {
                              analiseVisualizada.analises.imagemRadiologica
                                .trombosePortal.recomendacaoAnticoagulacao
                            }
                          </p>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ALERTAS CRÍTICOS */}
          {/* 🛡️ B223 — Pontos a conferir (rede de fidelidade, NÃO bloqueante) */}
          {analiseVisualizada.avisosFidelidade &&
            analiseVisualizada.avisosFidelidade.length > 0 && (
              <div
                style={{
                  backgroundColor: "white",
                  border: `2px solid #f59e0b`,
                  borderRadius: "12px",
                  padding: "24px",
                  fontFamily: theme.fonts.sans,
                }}
              >
                <h3
                  style={{
                    fontSize: "18px",
                    fontWeight: "bold",
                    color: theme.gray[900],
                    marginBottom: "6px",
                  }}
                >
                  <AlertTriangle size={16} style={{ marginRight: "8px" }} />{" "}
                  Pontos a conferir
                </h3>
                <p
                  style={{
                    fontSize: "13px",
                    color: theme.gray[600],
                    marginTop: 0,
                    marginBottom: "16px",
                    lineHeight: 1.5,
                  }}
                >
                  Conferência automática (sem IA) do texto da análise. São avisos{" "}
                  <strong>não bloqueantes</strong> — a análise não foi alterada;
                  apenas confira os pontos abaixo.
                </p>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                  }}
                >
                  {analiseVisualizada.avisosFidelidade.map((aviso, index) => (
                    <div
                      key={index}
                      style={{
                        padding: "12px 14px",
                        backgroundColor: "#fef3c7",
                        borderRadius: "8px",
                        border: `1px solid #f59e0b`,
                        fontSize: "13px",
                        color: theme.gray[800],
                        lineHeight: 1.5,
                      }}
                    >
                      {aviso.mensagem}
                    </div>
                  ))}
                </div>
              </div>
            )}

          {analiseVisualizada.resumo.alertasCriticos.length > 0 && (
            <div
              style={{
                backgroundColor: "white",
                border: `2px solid #ef4444`,
                borderRadius: "12px",
                padding: "24px",
                fontFamily: theme.fonts.sans,
              }}
            >
              <h3
                style={{
                  fontSize: "18px",
                  fontWeight: "bold",
                  color: theme.gray[900],
                  marginBottom: "16px",
                }}
              >
                <AlertTriangle size={16} style={{ marginRight: "8px" }} />{" "}
                Alertas Críticos
              </h3>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                }}
              >
                {analiseVisualizada.resumo.alertasCriticos.map(
                  (alerta, index) => (
                    <div
                      key={index}
                      style={{
                        padding: "16px",
                        backgroundColor:
                          alerta.tipo === "critico"
                            ? "#fee2e2"
                            : alerta.tipo === "atencao"
                            ? "#fef3c7"
                            : "#e0f2fe",
                        borderRadius: "8px",
                        border: `1px solid ${
                          alerta.tipo === "critico"
                            ? "#ef4444"
                            : alerta.tipo === "atencao"
                            ? "#f59e0b"
                            : "#0ea5e9"
                        }`,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          marginBottom: "8px",
                        }}
                      >
                        {alerta.categoria === "imagem" && (
                          <Camera size={16} style={{ marginRight: "6px" }} />
                        )}
                        {alerta.categoria === "tumor" && (
                          <Target size={16} style={{ marginRight: "6px" }} />
                        )}
                        {alerta.categoria === "hepatica" && (
                          <Hospital size={16} style={{ marginRight: "6px" }} />
                        )}
                        {alerta.categoria === "renal" && (
                          <Bean size={16} style={{ marginRight: "6px" }} />
                        )}
                        <h4
                          style={{
                            fontSize: "14px",
                            fontWeight: "600",
                            color: theme.gray[900],
                            margin: 0,
                          }}
                        >
                          {alerta.titulo}
                        </h4>
                      </div>
                      <p
                        style={{
                          fontSize: "14px",
                          color: theme.gray[700],
                          marginBottom: "8px",
                        }}
                      >
                        {alerta.descricao}
                      </p>
                      {alerta.valorAtual && (
                        <p
                          style={{
                            fontSize: "12px",
                            color: theme.gray[600],
                          }}
                        >
                          <strong>Valor:</strong> {alerta.valorAtual}
                          {alerta.valorReferencia &&
                            ` (Referência: ${alerta.valorReferencia})`}
                        </p>
                      )}
                    </div>
                  )
                )}
              </div>
            </div>
          )}

          {/* SUGESTÕES DE CONDUTA */}
          {analiseVisualizada.sugestoes &&
            analiseVisualizada.sugestoes.length > 0 && (
              <div
                style={{
                  backgroundColor: "white",
                  border: `2px solid ${theme.primary[600]}`,
                  borderRadius: "12px",
                  padding: "24px",
                  fontFamily: theme.fonts.sans,
                }}
              >
                <h3
                  style={{
                    fontSize: "18px",
                    fontWeight: "bold",
                    color: theme.gray[900],
                    marginBottom: "16px",
                  }}
                >
                  <Lightbulb size={16} style={{ marginRight: "8px" }} />{" "}
                  Sugestões de Conduta
                </h3>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "16px",
                  }}
                >
                  {analiseVisualizada.sugestoes.map((sugestao, index) => (
                    <div
                      key={index}
                      style={{
                        padding: "16px",
                        backgroundColor: theme.gray[50],
                        borderRadius: "8px",
                        borderLeft: `4px solid ${getCorPrioridade(
                          sugestao.prioridade
                        )}`,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: "12px",
                        }}
                      >
                        <h4
                          style={{
                            fontSize: "16px",
                            fontWeight: "600",
                            color: theme.gray[900],
                          }}
                        >
                          {sugestao.titulo}
                        </h4>
                        <span
                          style={{
                            padding: "4px 12px",
                            backgroundColor: getCorPrioridade(
                              sugestao.prioridade
                            ),
                            color: "white",
                            borderRadius: "12px",
                            fontSize: "12px",
                            fontWeight: "600",
                            textTransform: "uppercase",
                          }}
                        >
                          {sugestao.prioridade}
                        </span>
                      </div>
                      <p
                        style={{
                          fontSize: "14px",
                          fontWeight: "600",
                          color: theme.gray[800],
                          marginBottom: "8px",
                        }}
                      >
                        {sugestao.conduta}
                      </p>
                      <p
                        style={{
                          fontSize: "14px",
                          color: theme.gray[600],
                          marginBottom: "8px",
                        }}
                      >
                        {sugestao.justificativa}
                      </p>
                      {sugestao.prazo && (
                        <p
                          style={{
                            fontSize: "12px",
                            color: theme.gray[500],
                            fontStyle: "italic",
                          }}
                        >
                          ⏱️ {sugestao.prazo}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

          {/* ✅ NOVO: CORRELAÇÕES (se disponível) */}
          {analiseVisualizada.correlacoes &&
            analiseVisualizada.correlacoes.length > 0 && (
              <div
                style={{
                  backgroundColor: "white",
                  border: `1px solid ${theme.gray[300]}`,
                  borderRadius: "12px",
                  padding: "24px",
                  fontFamily: theme.fonts.sans,
                }}
              >
                <h3
                  style={{
                    fontSize: "18px",
                    fontWeight: "bold",
                    color: theme.gray[900],
                    marginBottom: "16px",
                  }}
                >
                  🔗 Correlações Clínicas
                </h3>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "12px",
                  }}
                >
                  {analiseVisualizada.correlacoes.map((corr, index) => (
                    <div
                      key={index}
                      style={{
                        padding: "12px 16px",
                        backgroundColor: theme.gray[50],
                        borderRadius: "8px",
                        borderLeft: `3px solid ${
                          corr.relevancia === "alta"
                            ? "#ef4444"
                            : corr.relevancia === "media"
                            ? "#f59e0b"
                            : theme.gray[400]
                        }`,
                      }}
                    >
                      <p
                        style={{
                          fontSize: "13px",
                          color: theme.gray[500],
                          marginBottom: "4px",
                        }}
                      >
                        {corr.tipo === "imagem_marcador" && (
                          <>
                            <Camera size={12} style={{ marginRight: "4px" }} />{" "}
                            Imagem + Marcador
                          </>
                        )}
                        {corr.tipo === "laboratorial_imagem" && (
                          <>
                            <FlaskConical
                              size={12}
                              style={{ marginRight: "4px" }}
                            />{" "}
                            Lab + Imagem
                          </>
                        )}
                        {corr.tipo === "clinico_laboratorial" && (
                          <>
                            <Stethoscope
                              size={12}
                              style={{ marginRight: "4px" }}
                            />{" "}
                            Clínico + Lab
                          </>
                        )}
                        {corr.tipo === "sintoma_exame" && (
                          <>
                            <ClipboardList
                              size={12}
                              style={{ marginRight: "4px" }}
                            />{" "}
                            Sintoma + Exame
                          </>
                        )}
                      </p>
                      <p
                        style={{
                          fontSize: "14px",
                          color: theme.gray[800],
                          fontWeight: "500",
                        }}
                      >
                        {corr.elementos.join(" ↔ ")}
                      </p>
                      <p
                        style={{
                          fontSize: "13px",
                          color: theme.gray[600],
                          marginTop: "4px",
                        }}
                      >
                        {corr.interpretacao}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

          {/* B229 (AI_147) — proveniência: fatos do prontuário em que a análise
              se apoia (labs + marcadores da coleta mais recente). Só exibição.
              B229 fase 2 / B328 (AI_148): recebe a análise p/ listar as diretrizes
              recuperadas do RAG (analiseVisualizada.documentosRag). */}
          <PainelProveniencia paciente={paciente} analise={analiseVisualizada} />

          {/* 🎯 PAINEL DE CALIBRAÇÃO DO HEPATOLOGISTA — 🔐 B88: calibrar é
              decisão clínica (calibracoes-semaforo só médico nas rules) */}
          {analiseVisualizada && ehMedico(usuarioLogado) && (
            <div
              style={{
                backgroundColor: calibracaoSalva ? "#f0fdf4" : "white",
                border: `1px solid ${
                  calibracaoSalva ? "#86efac" : theme.gray[300]
                }`,
                borderRadius: "12px",
                padding: "20px",
                fontFamily: theme.fonts.sans,
              }}
            >
              <h3
                style={{
                  fontSize: "16px",
                  fontWeight: "bold",
                  color: theme.gray[900],
                  marginBottom: "12px",
                }}
              >
                🎯 Calibração do Hepatologista
              </h3>
              {calibracaoSalva ? (
                <p
                  style={{
                    fontSize: "14px",
                    color: "#16a34a",
                    fontWeight: "500",
                  }}
                >
                  ✅ Avaliação registrada:{" "}
                  {calibracaoVoto === "concordo"
                    ? "Concordo"
                    : calibracaoVoto === "parcial"
                    ? "Parcialmente"
                    : "Discordo"}
                  {calibracaoObs && ` — "${calibracaoObs}"`}
                </p>
              ) : (
                <>
                  <p
                    style={{
                      fontSize: "13px",
                      color: theme.gray[600],
                      marginBottom: "8px",
                    }}
                  >
                    Você concorda com os semáforos e recomendações desta
                    análise?
                  </p>
                  <div
                    style={{
                      display: "flex",
                      gap: "16px",
                      marginBottom: "12px",
                      fontSize: "12px",
                      color: theme.gray[500],
                    }}
                  >
                    <span>🟢 Bem fundamentada</span>
                    <span>🟡 Avaliação complementar</span>
                    <span>🔴 Dados insuficientes/conflitantes</span>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      gap: "8px",
                      marginBottom: "12px",
                    }}
                  >
                    {[
                      {
                        valor: "concordo",
                        label: "Concordo",
                        icon: <ThumbsUp size={14} />,
                        cor: "#16a34a",
                        corFundo: "#f0fdf4",
                      },
                      {
                        valor: "parcial",
                        label: "Parcial",
                        icon: <MinusCircle size={14} />,
                        cor: "#d97706",
                        corFundo: "#fffbeb",
                      },
                      {
                        valor: "discordo",
                        label: "Discordo",
                        icon: <ThumbsDown size={14} />,
                        cor: "#dc2626",
                        corFundo: "#fef2f2",
                      },
                    ].map((opcao) => (
                      <button
                        key={opcao.valor}
                        onClick={() => setCalibracaoVoto(opcao.valor)}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          padding: "8px 16px",
                          borderRadius: "8px",
                          border: `2px solid ${
                            calibracaoVoto === opcao.valor
                              ? opcao.cor
                              : theme.gray[200]
                          }`,
                          backgroundColor:
                            calibracaoVoto === opcao.valor
                              ? opcao.corFundo
                              : "white",
                          color:
                            calibracaoVoto === opcao.valor
                              ? opcao.cor
                              : theme.gray[600],
                          fontWeight:
                            calibracaoVoto === opcao.valor ? "600" : "400",
                          cursor: "pointer",
                          fontSize: "13px",
                          transition: "all 0.15s",
                        }}
                      >
                        {opcao.icon} {opcao.label}
                      </button>
                    ))}
                  </div>
                  {/* B227 (AI_114): categoria do erro — só quando Parcial/Discordo */}
                  {(calibracaoVoto === "parcial" ||
                    calibracaoVoto === "discordo") && (
                    <div style={{ marginBottom: "12px" }}>
                      <label
                        style={{
                          display: "block",
                          fontSize: "12px",
                          fontWeight: 600,
                          color: theme.gray[600],
                          marginBottom: "4px",
                        }}
                      >
                        Categoria do erro (ajuda a calibrar a IA)
                      </label>
                      <select
                        value={calibracaoCategoria || ""}
                        onChange={(e) =>
                          setCalibracaoCategoria(e.target.value || null)
                        }
                        style={{
                          width: "100%",
                          padding: "8px 12px",
                          borderRadius: "8px",
                          border: `1px solid ${theme.gray[300]}`,
                          fontSize: "13px",
                          fontFamily: theme.fonts.sans,
                          backgroundColor: "white",
                          outline: "none",
                        }}
                      >
                        <option value="">Selecione…</option>
                        {CATEGORIAS_ERRO_CALIBRACAO.map((c) => (
                          <option key={c.valor} value={c.valor}>
                            {c.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  <div style={{ display: "flex", gap: "8px" }}>
                    <input
                      type="text"
                      placeholder="Observação (opcional)"
                      value={calibracaoObs}
                      onChange={(e) => setCalibracaoObs(e.target.value)}
                      style={{
                        flex: 1,
                        padding: "8px 12px",
                        borderRadius: "8px",
                        border: `1px solid ${theme.gray[300]}`,
                        fontSize: "13px",
                        fontFamily: theme.fonts.sans,
                        outline: "none",
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && calibracaoVoto)
                          salvarCalibracao();
                      }}
                    />
                    <button
                      onClick={salvarCalibracao}
                      disabled={!calibracaoVoto || salvandoCalibracao}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        padding: "8px 16px",
                        borderRadius: "8px",
                        border: "none",
                        backgroundColor: calibracaoVoto
                          ? theme.primary[600]
                          : theme.gray[200],
                        color: calibracaoVoto ? "white" : theme.gray[400],
                        fontWeight: "600",
                        cursor: calibracaoVoto ? "pointer" : "not-allowed",
                        fontSize: "13px",
                      }}
                    >
                      <Send size={14} />{" "}
                      {salvandoCalibracao ? "Salvando..." : "Registrar"}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* ESTADO VAZIO */}
      {!loading && !analiseVisualizada && !erro && (
        <div
          style={{
            padding: "64px 24px",
            textAlign: "center",
            backgroundColor: theme.gray[50],
            borderRadius: "12px",
            fontFamily: theme.fonts.sans,
          }}
        >
          <Bot
            size={64}
            color={theme.gray[300]}
            style={{ marginBottom: "16px" }}
          />
          <h3
            style={{
              fontSize: "20px",
              fontWeight: "600",
              color: theme.gray[900],
              marginBottom: "8px",
            }}
          >
            Nenhuma análise gerada ainda
          </h3>
          <p
            style={{
              fontSize: "14px",
              color: theme.gray[600],
              marginBottom: "24px",
            }}
          >
            Clique no botão "Gerar Nova Análise" para iniciar a análise
            integrada do paciente
          </p>
          <p
            style={{
              fontSize: "13px",
              color: theme.gray[500],
            }}
          >
            A análise incluirá: consultas, exames laboratoriais, marcadores
            tumorais e achados de imagem
          </p>
        </div>
      )}
      {/* MODAL DO CHAT */}
      {chatAberto && (
        <ChatDiscussaoCaso
          paciente={paciente}
          onClose={() => setChatAberto(false)}
        />
      )}
      {/* MODAL DO LAUDO ESPECIAL — B250 (AI_113) */}
      {laudoEspecialAberto && (
        <LaudoEspecial
          paciente={paciente}
          onClose={() => setLaudoEspecialAberto(false)}
        />
      )}
    </div>
  );
}
